import React, { useEffect, useMemo, useRef, useState } from "react";
import { PageHeader, StatusBadge, EmptyState } from "@/components/common";
import { api, formatApiErrorDetail, BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Upload, Pencil, AlertTriangle, CheckCircle2, LayoutGrid } from "lucide-react";
import { useAuth } from "@/lib/auth";

const kb = (n) => `${(n / 1024).toFixed(1)} KB`;

// Reads the natural image dimensions from a File. Resolves with {width,height}.
function readImageDims(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Görsel okunamadı"));
    };
    img.src = url;
  });
}

export default function CreativesPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [muns, setMuns] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterMun, setFilterMun] = useState("all");
  const [filterCamp, setFilterCamp] = useState("all");

  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ id: null, campaign_id: "", ad_size_id: "", alt_text: "", target_url: "", status: "active", weight: 5 });
  const [file, setFile] = useState(null);
  const [fileDims, setFileDims] = useState(null); // {width,height} of newly-selected file
  const [dimsError, setDimsError] = useState(""); // human-readable mismatch message
  const [dimsChecking, setDimsChecking] = useState(false);
  const fileInputRef = useRef(null);

  const isEdit = !!form.id;

  async function load() {
    setLoading(true);
    try {
      const [c, cp, m, sz] = await Promise.all([
        api.get("/admin/creatives"),
        api.get("/admin/campaigns"),
        api.get("/admin/municipalities"),
        api.get("/admin/ad-sizes"),
      ]);
      setRows(c.data); setCampaigns(cp.data); setMuns(m.data); setSizes(sz.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  // When municipality filter changes, if the current campaign filter no longer
  // belongs to that municipality, reset it to "all".
  useEffect(() => {
    if (filterMun === "all") return;
    if (filterCamp === "all") return;
    const c = campaigns.find((x) => x.id === filterCamp);
    if (!c || c.municipality_id !== filterMun) setFilterCamp("all");
  }, [filterMun, filterCamp, campaigns]);

  const campaignsForMun = useMemo(() => (
    filterMun === "all" ? campaigns : campaigns.filter((c) => c.municipality_id === filterMun)
  ), [campaigns, filterMun]);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (filterMun !== "all") out = out.filter((r) => r.municipality_id === filterMun);
    if (filterCamp !== "all") out = out.filter((r) => r.campaign_id === filterCamp);
    return out;
  }, [rows, filterMun, filterCamp]);

  // Group by campaign, then within each group sort by alphabetical alt_text
  // (fallback: size name). Groups themselves are ordered by campaign name.
  const grouped = useMemo(() => {
    const g = new Map();
    for (const r of filteredRows) {
      const key = r.campaign_id;
      if (!g.has(key)) {
        g.set(key, {
          campaign_id: key,
          campaign_name: r.campaign_name || "—",
          municipality_name: r.municipality_name || "",
          items: [],
        });
      }
      g.get(key).items.push(r);
    }
    const arr = [...g.values()];
    const trCollator = new Intl.Collator("tr");
    arr.sort((a, b) => trCollator.compare(a.campaign_name, b.campaign_name));
    for (const gg of arr) {
      gg.items.sort((a, b) => {
        const an = (a.alt_text || a.ad_size?.name || "").toLowerCase();
        const bn = (b.alt_text || b.ad_size?.name || "").toLowerCase();
        return trCollator.compare(an, bn);
      });
    }
    return arr;
  }, [filteredRows]);

  function resetFileState() {
    setFile(null); setFileDims(null); setDimsError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function openNew() {
    setForm({ id: null, campaign_id: campaigns[0]?.id || "", ad_size_id: sizes[0]?.id || "", alt_text: "", target_url: "", status: "active", weight: 5 });
    resetFileState();
    setOpen(true);
  }
  function openEdit(r) {
    setForm({
      id: r.id,
      campaign_id: r.campaign_id,
      ad_size_id: r.ad_size_id,
      alt_text: r.alt_text || "",
      target_url: r.target_url || "",
      status: r.status || "active",
      weight: r.weight ?? 5,
    });
    resetFileState();
    setOpen(true);
  }

  // Whenever file or ad_size_id changes, re-validate dimensions against the target size.
  async function handleFileChange(f) {
    setFile(f);
    setFileDims(null);
    setDimsError("");
    if (!f) return;
    setDimsChecking(true);
    try {
      const dims = await readImageDims(f);
      setFileDims(dims);
      validateDims(dims, form.ad_size_id);
    } catch (e) {
      setDimsError(e.message || "Görsel okunamadı");
    } finally {
      setDimsChecking(false);
    }
  }

  function validateDims(dims, sizeId) {
    if (!dims || !sizeId) { setDimsError(""); return; }
    const sz = sizes.find((s) => s.id === sizeId);
    if (!sz) return;
    if (dims.width !== sz.width || dims.height !== sz.height) {
      setDimsError(`Boyut uyumsuz: yüklenen ${dims.width}×${dims.height} px · beklenen ${sz.width}×${sz.height} px. Görsel yeniden ölçeklendirilmeli veya doğru boyutta olan bir dosya seçilmelidir.`);
    } else {
      setDimsError("");
    }
  }

  // Re-validate when the ad_size_id changes (edit mode: user might switch size).
  useEffect(() => {
    if (fileDims) validateDims(fileDims, form.ad_size_id);
  }, [form.ad_size_id]);

  const canSave = (() => {
    if (uploading || dimsChecking) return false;
    if (!form.campaign_id || !form.ad_size_id) return false;
    if (isEdit) {
      // In edit mode a new file is optional; but if provided, dimensions MUST match.
      if (file && dimsError) return false;
      return true;
    }
    // Create mode: file is required and dimensions must match exactly.
    if (!file) return false;
    if (dimsError) return false;
    return true;
  })();

  async function save() {
    if (!canSave) return;
    const fd = new FormData();
    fd.append("campaign_id", form.campaign_id);
    fd.append("ad_size_id", form.ad_size_id);
    fd.append("alt_text", form.alt_text || "");
    fd.append("target_url", form.target_url || "");
    fd.append("weight", String(form.weight || 5));
    if (isEdit) fd.append("status", form.status);
    if (file) fd.append("file", file);
    setUploading(true);
    try {
      if (isEdit) {
        await api.put(`/admin/creatives/${form.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(file ? "Kreatif güncellendi ve görsel değiştirildi" : "Kreatif bilgileri güncellendi");
      } else {
        await api.post("/admin/creatives", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Kreatif yüklendi ve WebP formatına dönüştürüldü");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setUploading(false); }
  }

  async function remove(r) {
    const msg = [
      "Bu kreatif kalıcı olarak silinsin mi?",
      "",
      "• Görsel dosyaları (webp + jpg) storage'dan silinir.",
      "• Bu işlem geri alınamaz.",
      "• Geçmiş istatistikler raporlarda kreatif adıyla görünmeye devam eder.",
    ].join("\n");
    if (!window.confirm(msg)) return;
    try { await api.delete(`/admin/creatives/${r.id}`); toast.success("Silindi · storage temizlendi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  const selectedSize = sizes.find((s) => s.id === form.ad_size_id);

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Görsel Yönetimi"
        title="Kreatifler"
        description="Reklam görselleri otomatik olarak WebP formatına dönüştürülür ve CDN üzerinden servis edilir. Yanlış girilen bilgi ve görseller silmeden düzenlenebilir."
        actions={isSuper && (
          <Button className="bg-red-600 hover:bg-red-700 rounded-sm h-10 font-heading font-semibold uppercase text-xs tracking-wider" onClick={openNew} data-testid="upload-creative-button">
            <Upload className="w-4 h-4 mr-1.5" /> Görsel Yükle
          </Button>
        )}
      />

      {/* Filter bar: belediye + kampanya */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-600">Belediye</Label>
          <Select value={filterMun} onValueChange={setFilterMun}>
            <SelectTrigger className="w-56 h-9" data-testid="creatives-mun-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü ({rows.length})</SelectItem>
              {muns.map((m) => {
                const cnt = rows.filter((r) => r.municipality_id === m.id).length;
                return <SelectItem key={m.id} value={m.id}>{m.name} ({cnt})</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-600">Kampanya</Label>
          <Select value={filterCamp} onValueChange={setFilterCamp}>
            <SelectTrigger className="w-64 h-9" data-testid="creatives-camp-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü ({filteredRows.length})</SelectItem>
              {campaignsForMun.map((c) => {
                const cnt = rows.filter((r) => r.campaign_id === c.id).length;
                return <SelectItem key={c.id} value={c.id}>{c.name} ({cnt})</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        {(filterMun !== "all" || filterCamp !== "all") && (
          <Button variant="ghost" size="sm" className="h-9" onClick={() => { setFilterMun("all"); setFilterCamp("all"); }} data-testid="creatives-filter-reset">
            Filtreyi Temizle
          </Button>
        )}
        <div className="ml-auto text-[11px] text-slate-500 inline-flex items-center gap-1">
          <LayoutGrid className="w-3.5 h-3.5" /> Kampanya bazlı gruplu görünüm · Türkçe alfabetik sıra
        </div>
      </div>

      {loading ? <div className="text-sm text-slate-500">Yükleniyor…</div> :
        rows.length === 0 ? <EmptyState title="Henüz kreatif yok" subtitle="Yukarıdaki 'Görsel Yükle' butonu ile başlayın." testid="creatives-empty" /> :
        filteredRows.length === 0 ? <EmptyState title="Bu filtreye uyan kreatif yok" subtitle="Belediye veya kampanya seçimini değiştirin." testid="creatives-filter-empty" /> :
        (
          <div className="space-y-6" data-testid="creatives-grouped">
            {grouped.map((g) => (
              <div key={g.campaign_id} className="space-y-3" data-testid={`camp-group-${g.campaign_id}`}>
                <div className="flex items-baseline justify-between border-b border-slate-200 pb-2">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-widest text-slate-500">{g.municipality_name}</div>
                    <h3 className="font-heading font-semibold text-lg text-slate-950">{g.campaign_name}</h3>
                  </div>
                  <span className="text-xs text-slate-500">{g.items.length} kreatif</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {g.items.map((r) => (
                    <div key={r.id} className="bg-white border border-slate-200 rounded-md overflow-hidden group" data-testid={`creative-card-${r.id}`}>
                      <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center overflow-hidden">
                        <img src={`${BASE}${r.webp_url}`} alt={r.alt_text || ""} className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="p-3 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-[10px] uppercase tracking-widest text-slate-500">{r.ad_size?.name || ""}</div>
                          <StatusBadge status={r.effective_status || r.status} />
                        </div>
                        <div className="text-sm font-medium text-slate-900 truncate">{r.alt_text || r.campaign_name}</div>
                        <div className="mt-2 text-[11px] text-slate-500 flex items-center justify-between">
                          <span className="font-mono">WebP {kb(r.file_size_webp || 0)}</span>
                          <span className="font-mono">Ağırlık: {r.weight ?? 5}</span>
                        </div>
                        {isSuper && (
                          <div className="mt-3 flex items-center justify-between gap-1.5">
                            <Button size="sm" variant="outline" className="h-7 rounded-sm text-slate-700" onClick={() => openEdit(r)} data-testid={`edit-creative-${r.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-1" /> Düzenle
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 h-7" onClick={() => remove(r)} data-testid={`delete-creative-${r.id}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Sil
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetFileState(); }}>
        <DialogContent className="max-w-md rounded-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{isEdit ? "Kreatifi Düzenle" : "Kreatif Yükle"}</DialogTitle>
            <DialogDescription>{isEdit ? "Kreatif meta bilgilerini ve isteğe bağlı olarak görselin kendisini güncelleyin." : "Görsel dosyası ile birlikte kampanya, ölçü ve hedef URL bilgilerini girin. Boyut kesin eşleşme gerektirir."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Kampanya</Label>
              <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
                <SelectTrigger data-testid="creative-form-campaign"><SelectValue /></SelectTrigger>
                <SelectContent>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reklam Ölçüsü</Label>
              <Select value={form.ad_size_id} onValueChange={(v) => setForm({ ...form, ad_size_id: v })}>
                <SelectTrigger data-testid="creative-form-size"><SelectValue /></SelectTrigger>
                <SelectContent>{sizes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              {selectedSize && (
                <div className="text-[10.5px] text-slate-500 mt-1">
                  Beklenen boyut: <b className="font-mono">{selectedSize.width}×{selectedSize.height} px</b> (kesin eşleşme zorunlu, ±1 px sapma bile kabul edilmez)
                </div>
              )}
            </div>
            <div><Label>Alternatif Metin</Label><Input value={form.alt_text} onChange={(e) => setForm({ ...form, alt_text: e.target.value })} placeholder="Reklam açıklaması" data-testid="creative-form-alt" /></div>
            <div><Label>Hedef URL (opsiyonel — boşsa kampanya URL&apos;i kullanılır)</Label><Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://" data-testid="creative-form-target" /></div>

            {isEdit && (
              <div>
                <Label>Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="creative-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="paused">Duraklatıldı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Rotasyon Ağırlığı (1–10)</Label>
              <Input
                type="number" min="1" max="10"
                value={form.weight}
                onChange={(e) => setForm({ ...form, weight: parseInt(e.target.value, 10) || 5 })}
                data-testid="creative-form-weight"
              />
              <div className="text-[10.5px] text-slate-500 mt-1">
                Aynı belediye × ölçüde birden fazla aktif kreatif varsa, her sayfa yüklemesinde ağırlığa göre rasgele biri gösterilir. Örnek: 3 ile 7 → 7 olan yaklaşık 2.3 kat daha sık gösterilir.
              </div>
            </div>

            <div>
              <Label>{isEdit ? "Görseli Değiştir (opsiyonel · JPG/PNG/WebP · maks 5 MB)" : "Görsel Dosyası (JPG/PNG/WebP · maks 5 MB)"}</Label>
              <Input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFileChange(e.target.files?.[0] || null)} data-testid="creative-form-file" />
              {file && (
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <span>{file.name} — {kb(file.size)}</span>
                  {fileDims && <span className="font-mono">· {fileDims.width}×{fileDims.height} px</span>}
                </div>
              )}
              {dimsChecking && <div className="text-[11px] text-slate-500 mt-1">Boyut kontrol ediliyor…</div>}
              {dimsError && (
                <div className="mt-2 text-[11px] leading-relaxed bg-red-50 border border-red-200 text-red-700 rounded p-2 flex items-start gap-1.5" data-testid="creative-dim-error">
                  <AlertTriangle className="w-3.5 h-3.5 mt-[1px] shrink-0" />
                  <span>{dimsError}</span>
                </div>
              )}
              {file && !dimsError && !dimsChecking && fileDims && (
                <div className="mt-2 text-[11px] leading-relaxed bg-emerald-50 border border-emerald-200 text-emerald-700 rounded p-2 flex items-start gap-1.5" data-testid="creative-dim-ok">
                  <CheckCircle2 className="w-3.5 h-3.5 mt-[1px] shrink-0" />
                  <span>Boyut doğru — yüklemeye hazır.</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button disabled={!canSave} onClick={save} className="bg-red-600 hover:bg-red-700 rounded-sm disabled:bg-slate-300 disabled:cursor-not-allowed" data-testid="creative-form-save">
              {uploading ? "Yükleniyor…" : (isEdit ? "Güncelle" : "Yükle")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
